import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import {
  Background,
  BackgroundVariant,
  Controls,
  ReactFlow,
  useEdgesState,
  useNodesState
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useNavigate } from 'react-router-dom';
import useAppStore from '../../store';
import { buildBrainGraph, BRAIN_STATS } from '../../mocks/brain';
import { layoutBrainGraph } from '../../lib/formatters/brainLayout';
import { NODE_TYPES } from '../../components/brain/BrainNodes';
import NodeDetailSidebar from '../../components/brain/NodeDetailSidebar';
import OrchestratorStatsBar from '../../components/brain/OrchestratorStatsBar';
import DataModeBadge from '../../components/feedback/DataModeBadge';

/**
 * T13.7 perf guard: edges animate ONLY while the source agent is active
 * (running/autonomous). Idle graph = zero CSS animation work. Applied in
 * the status-sync effect below via `edge.animated`.
 */
function BrainPage() {
  const agents = useAppStore((state) => state.agents);
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState(null);

  const { initialNodes, initialEdges } = useMemo(() => {
    const graph = buildBrainGraph();
    return {
      initialNodes: layoutBrainGraph(graph.nodes, graph.edges),
      initialEdges: graph.edges
    };
  }, []);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Reflect live agent status onto graph nodes + edge animation (perf guard).
  useEffect(() => {
    setNodes((current) => current.map((node) => ({
      ...node,
      data: {
        ...node.data,
        status: agents.find((agent) => `agent-${agent.id}` === node.id)?.status ?? node.data.status
      }
    })));
    setEdges((current) => current.map((edge) => ({
      ...edge,
      animated: ['running', 'autonomous'].includes(
        agents.find((agent) => `agent-${agent.id}` === edge.source)?.status ?? ''
      )
    })));
  }, [agents, setNodes, setEdges]);

  const selectedNode = useMemo(
    () => nodes.find((node) => node.id === selectedId) ?? null,
    [nodes, selectedId]
  );

  const handleNodeClick = useCallback((event, node) => {
    setSelectedId(node.id);
  }, []);

  const activeCount = agents.filter(
    (agent) => agent.status === 'running' || agent.status === 'autonomous'
  ).length;

  return (
    <main className="page-content brain-page">
      <section className="page-heading">
        <div>
          <p className="eyebrow">ORCHESTRATION GRAPH</p>
          <h1>
            The brain <DataModeBadge mode="sample" label="Sample data — outcomes loop pending" />
          </h1>
          <span>
            Every decision Agent 0 makes, traced across the live agent graph —
            {BRAIN_STATS.decisions.toLocaleString()} decisions and counting.
          </span>
        </div>
      </section>

      {/* Cutover §3 explicit exception (blocked on the T2 outcomes loop): this
          screen is a PREVIEW. The banner states it before any number is read;
          the stats bar and graph stay visible beneath as sample data — only
          the fleet-active tile reflects the live roster. */}
      <section className="brain-preview-banner" role="note">
        <FiIcons.FiEyeOff aria-hidden="true" />
        <div>
          <strong>Preview — learning loop not yet live</strong>
          <span>
            The outcomes loop (T2) has no writers in this build: the decision
            counts, autonomy rate, and graph activity below are sample data.
            Blocked on T2, not wired by cutover §3 — this banner replaces the
            silent stale mock.
          </span>
        </div>
      </section>

      <OrchestratorStatsBar activeCount={activeCount} totalAgents={agents.length} />

      <section className="brain-canvas-wrap">
        <div className="brain-canvas">
          <ReactFlow
            edges={edges}
            nodes={nodes}
            nodeTypes={NODE_TYPES}
            nodesConnectable={false}
            onNodeClick={handleNodeClick}
            proOptions={{ hideAttribution: true }}
            zoomOnScroll
          >
            <Background gap={28} size={1} variant={BackgroundVariant.Dots} />
            <Controls showInteractive={false} />
          </ReactFlow>
        </div>

        <NodeDetailSidebar
          agents={agents}
          node={selectedNode}
          onClose={() => setSelectedId(null)}
          onOpenAgent={(agentId) => navigate(`/agents/${agentId}`)}
        />
      </section>
    </main>
  );
}

export default BrainPage;
